import { createShopApplication } from 'admin/actions/shop-applications';
import { showShop } from 'admin/actions/shops';
import {
  getActiveUserTagGroupsWithItems,
  NormalizedTagGroup,
} from 'admin/actions/user-tag-group';
import { fetchUserTags, UserTag } from 'admin/actions/user-tags';
import { Button } from 'admin/components/atoms/Buttons';
import Grid from 'admin/components/atoms/Grid';
import Label from 'admin/components/atoms/Label';
import BreadCrumb, { CrumbType } from 'admin/components/molecules/BreadCrumb';
import TagSelectionEditor, { TagSelection } from 'admin/components/organisms/TagSelectionEditor';
import { USAGE_TYPE_OPTIONS, UsageType } from 'admin/constants/usageType';
import { EXCLUDED_ISSUER_ORG_CODE } from 'admin/constants/userTagGroupLabels';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Alert } from 'reactstrap';
import styled, { css } from 'styled-components';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import toKatakana from '~/javascripts/admin/utils/characters/toKatakana';
import { validateEmail } from '~/javascripts/admin/utils/email';
import { pickIssuerOrgCodeFromMoneys } from '~/javascripts/admin/utils/shopFormValidation';
import Headings from '../../atoms/Headings';
import Loader from '../../atoms/Loader';
import Radio from '../../atoms/Radio';
import Required from '../../atoms/Required';
import Select from '../../atoms/Select';
import TextBox from '../../atoms/TextBox';
import ConfirmModal from './ConfirmModal';

const CRUMBS_CREATE: CrumbType[] = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '店舗管理', href: '', isCurrentPage: true },
  { value: '店舗追加申請', href: '', isCurrentPage: true },
];

const buildChangeCrumbs = (targetShopId?: string): CrumbType[] => [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '店舗管理', href: '', isCurrentPage: true },
  { value: '店舗一覧', href: '/shops', isCurrentPage: false },
  { value: '店舗詳細', href: `/shops/${targetShopId}`, isCurrentPage: false },
  { value: '店舗変更申請', href: '', isCurrentPage: true },
];

const STATUS_OPTIONS = [
  { label: '有効', value: 'active' },
  { label: '無効', value: 'disabled' },
];

const Wrapper = styled.div`
  margin-top: 24px;
`;

const FormGrid = styled(Grid.Container)`
  ${Label} {
    margin-bottom: 0;
    padding: 5px 0;
    ${(props) => props.theme.typography.body2.regular}
  }

  ${Grid.Divider} {
    margin: 12px 0;
  }
`;

const SectionLabel = styled(Label)`
  font-weight: 600;
`;

const FieldError = styled.p`
  ${({ theme }) => css`
    ${theme.typography.body3.regular}
    margin: 2px 0;
    color: ${theme.semanticColors.accent.red};
  `}
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 24px;
`;

const AlertWrapper = styled.div`
  margin-top: 16px;
  margin-bottom: 24px;
`;

const LoaderWrapper = styled.div`
  width: 100%;
  height: 200px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const RadioGroup = styled.div`
  display: flex;
  gap: 24px;
`;

const RadioLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
`;

type PrivateMoneyOption = {
  label: string;
  value: { id: string; name: string; organization?: { code: string } };
};

// 確認モーダルに渡すタグ表示用サマリ（グループ名・選択ラベル名・変更有無）
export type TagSummaryEntry = {
  groupId: string;
  groupName: string;
  itemNames: string[];
  changed: boolean;
};

export type ShopApplicationFormValues = {
  private_money_ids: string[];
  shop_name: string;
  shop_name_furigana: string;
  postal_code: string;
  address: string;
  tel: string;
  email: string;
  contact_person_name: string;
  contact_person_name_furigana: string;
  // 空文字 = 未選択（フロント必須）。送信時は必ず4種のいずれか。
  usage_type: UsageType | '';
};

type ShopApplicationFormProps = {
  mode: 'create' | 'change';
  targetShopId?: string;
};

const ShopApplicationForm = ({ mode, targetShopId }: ShopApplicationFormProps) => {
  const user = useAppSelector((state) => state.user);
  const selectedPrivateMoney = useAppSelector(selectUserPrivateMoney);

  // Loading state for change mode
  const [isLoadingShop, setIsLoadingShop] = useState(mode === 'change');
  const [loadError, setLoadError] = useState<string | null>(null);

  // Extra fields for change mode (read-only)
  const [externalId, setExternalId] = useState<string>('');
  const [shopStatus, setShopStatus] = useState<string>('active');
  const [changeType, setChangeType] = useState<'update' | 'close'>('update');

  // Original values for change mode (to detect changes)
  const [originalValues, setOriginalValues] = useState<ShopApplicationFormValues | null>(null);

  // Submission state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<'success' | 'error' | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingFormValues, setPendingFormValues] = useState<ShopApplicationFormValues | null>(
    null
  );

  // Tag group state（全有効グループ＋付与選択。RHF外で管理）
  const [availableTagGroups, setAvailableTagGroups] = useState<NormalizedTagGroup[]>([]);
  const [tagSelections, setTagSelections] = useState<TagSelection[]>([]);
  // change モードの既存タグ [{ tagId, groupId, itemId }] と初期選択（差分表示用）
  const [existingTags, setExistingTags] = useState<
    { tagId: string; groupId: string; itemId: string }[]
  >([]);
  const [originalTagSelections, setOriginalTagSelections] = useState<TagSelection[]>([]);
  const [tagGroupsIssuerOrgCode, setTagGroupsIssuerOrgCode] = useState<string | null>(null);
  const [tagGroupsLoading, setTagGroupsLoading] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ShopApplicationFormValues>({
    defaultValues: {
      private_money_ids: [],
      shop_name: '',
      shop_name_furigana: '',
      postal_code: '',
      address: '',
      tel: '',
      email: '',
      contact_person_name: '',
      contact_person_name_furigana: '',
      usage_type: '',
    },
  });

  // Private money options
  const moneys = user.moneys?.() ?? [];
  const privateMoneyOptions: PrivateMoneyOption[] = moneys.map((money: any) => ({
    label: money.name,
    value: money,
  }));

  const currentPrivateMoneyIds = watch('private_money_ids');
  const selectedMoneys = moneys.filter((m: any) => currentPrivateMoneyIds.includes(m.id));

  // Load shop details for change mode
  useEffect(() => {
    const loadShopDetails = async () => {
      if (mode !== 'change' || !targetShopId) return;

      setIsLoadingShop(true);
      setLoadError(null);

      try {
        const shop = await showShop(targetShopId);

        // Get private money IDs from shop accounts
        // Filter Pokepay Money since it is a special money
        const shopPrivateMoneyIds =
          shop.accounts
            ?.filter((acc) => acc.private_money?.organization?.code !== 'pocketchange')
            ?.map((acc: any) => acc.private_money?.id)
            .filter(Boolean) || [];

        // Fetch user tags to get existing shopping district area and industry category
        // Find the issuer account to get the private_money_id for the tags query
        const issuerAccount = (shop.accounts || []).find(
          (acc: any) =>
            acc.private_money?.organization?.code &&
            acc.private_money.organization.code !== EXCLUDED_ISSUER_ORG_CODE
        );
        const tagsQuery = issuerAccount?.private_money?.id
          ? `private_money_id=${issuerAccount.private_money.id}`
          : '';

        try {
          const userTagsResponse = await fetchUserTags(targetShopId, tagsQuery);
          const userTags: UserTag[] = userTagsResponse.rows;
          setExistingTags(
            userTags.map((t) => ({
              tagId: t.id,
              groupId: t.user_tag_group.id,
              itemId: t.user_tag_group_item.id,
            }))
          );
        } catch (tagErr) {
          console.error('Failed to fetch user tags', tagErr);
          // Continue without tags - they're optional
        }

        const initialValues: ShopApplicationFormValues = {
          private_money_ids: shopPrivateMoneyIds,
          shop_name: shop.name || '',
          shop_name_furigana: shop.shop_name_furigana || '',
          postal_code: shop.postal_code || '',
          address: shop.address || '',
          tel: shop.tel || '',
          email: shop.email || '',
          contact_person_name: shop.contact_person_name || '',
          contact_person_name_furigana: shop.contact_person_name_furigana || '',
          usage_type: shop.usage_type ?? '',
        };

        reset(initialValues);
        setOriginalValues(initialValues);

        // Set extra fields for change mode
        setExternalId(shop.external_id || '');
        setShopStatus(shop.status || 'active');
      } catch (err) {
        console.error('Failed to fetch shop details', err);
        setLoadError('店舗情報の取得に失敗しました');
      } finally {
        setIsLoadingShop(false);
      }
    };

    loadShopDetails();
  }, [mode, targetShopId, reset]);

  // Load tag groups when selected moneys change
  const tagReqRef = useRef(0);
  const loadTagGroups = useCallback(
    async (issuerOrgCode: string | null) => {
      if (issuerOrgCode === tagGroupsIssuerOrgCode) return;

      if (!issuerOrgCode) {
        tagReqRef.current += 1;
        setTagGroupsIssuerOrgCode(null);
        setAvailableTagGroups([]);
        return;
      }

      setTagGroupsIssuerOrgCode(issuerOrgCode);
      setTagGroupsLoading(true);
      // 組織切替時に古いレスポンスで上書きしないための stale ガード
      const reqId = (tagReqRef.current += 1);

      try {
        const groups = await getActiveUserTagGroupsWithItems(issuerOrgCode);
        if (reqId !== tagReqRef.current) return;
        setAvailableTagGroups(groups);
      } catch (err) {
        console.error('Failed to fetch user tag groups', err);
        if (reqId === tagReqRef.current) setTagGroupsIssuerOrgCode(null);
      } finally {
        if (reqId === tagReqRef.current) setTagGroupsLoading(false);
      }
    },
    [tagGroupsIssuerOrgCode]
  );

  useEffect(() => {
    const issuerOrgCode = pickIssuerOrgCodeFromMoneys(selectedMoneys);
    loadTagGroups(issuerOrgCode);
  }, [selectedMoneys, loadTagGroups]);

  // 既存タグ(change モード)を有効グループ単位に集約し、初期選択を生成する。
  // 発行体が変わってグループが空になった場合は、前発行体の選択を残さずクリアする。
  useEffect(() => {
    if (availableTagGroups.length === 0) {
      setTagSelections([]);
      setOriginalTagSelections([]);
      return;
    }
    const activeGroupIds = availableTagGroups.map((g) => g.id);
    const selections: TagSelection[] = availableTagGroups
      .map((group) => ({
        groupId: group.id,
        itemIds: existingTags.filter((e) => e.groupId === group.id).map((e) => e.itemId),
      }))
      .filter((row) => row.itemIds.length > 0 && activeGroupIds.includes(row.groupId));
    setTagSelections(selections);
    setOriginalTagSelections(selections);
  }, [availableTagGroups, existingTags]);

  // Check if user is a member organization user (not the issuer of the selected private money)
  const isMemberOrgUser = !!selectedPrivateMoney && !user.isIssuerOfMoney?.(selectedPrivateMoney);

  const isMoneyEmpty = currentPrivateMoneyIds.length === 0;

  // Get display names for modal
  const getPrivateMoneyNames = (ids: string[]) => {
    return moneys.filter((m: any) => ids.includes(m.id)).map((m: any) => m.name);
  };

  const arrayEq = (a: string[], b: string[]) => {
    if (a.length !== b.length) return false;
    const sortedA = [...a].sort();
    const sortedB = [...b].sort();
    return sortedA.every((v, i) => v === sortedB[i]);
  };

  // 確認モーダル用のタグサマリ。change モードでは「現在＋元」の和集合を出し、
  // 全解除されたグループも「変更あり・ラベルなし」で可視化する。
  const tagSummaryGroupIds = Array.from(
    new Set(
      [
        ...tagSelections.map((r) => r.groupId),
        ...originalTagSelections.map((r) => r.groupId),
      ].filter((id): id is string => !!id)
    )
  );
  const tagSummary: TagSummaryEntry[] = tagSummaryGroupIds
    .map((groupId) => {
      const group = availableTagGroups.find((g) => g.id === groupId);
      const currentIds = tagSelections.find((r) => r.groupId === groupId)?.itemIds ?? [];
      const originalIds = originalTagSelections.find((r) => r.groupId === groupId)?.itemIds ?? [];
      const itemNames = currentIds
        .map((id) => group?.items.find((it) => it.id === id)?.name)
        .filter((n): n is string => !!n);
      const changed = mode === 'change' && !arrayEq(currentIds, originalIds);
      return { groupId, groupName: group?.name ?? '', itemNames, changed };
    })
    // create モードでは空グループは表示しない（change モードは削除可視化のため残す）
    .filter((entry) => mode === 'change' || entry.itemNames.length > 0);

  // Open confirm modal instead of submitting directly
  const onSubmit = (values: ShopApplicationFormValues) => {
    setPendingFormValues(values);
    setIsModalOpen(true);
  };

  // Handle actual submission when user confirms
  const handleConfirmSubmit = async () => {
    if (!pendingFormValues) return;

    setIsSubmitting(true);
    setSubmitResult(null);
    setErrorMessage(null);

    try {
      // Build user_tag_group_item_ids from selected tag items
      const userTagGroupItemIds = tagSelections.flatMap((row) => row.itemIds).filter(Boolean);

      const payload: Record<string, unknown> = {
        shop_name: pendingFormValues.shop_name,
        shop_name_furigana: pendingFormValues.shop_name_furigana,
        postal_code: pendingFormValues.postal_code,
        address: pendingFormValues.address,
        tel: pendingFormValues.tel,
        email: pendingFormValues.email,
        contact_person_name: pendingFormValues.contact_person_name,
        contact_person_name_furigana: pendingFormValues.contact_person_name_furigana,
        private_money_ids: pendingFormValues.private_money_ids,
        usage_type: pendingFormValues.usage_type,
      };

      if (userTagGroupItemIds.length > 0) {
        payload.user_tag_group_item_ids = userTagGroupItemIds;
      }

      // For create mode: send organization_code
      // For change mode: send target_shop_id, and kind="disable" if closing shop
      if (mode === 'create') {
        payload.organization_code = user.organization.code;
      } else if (mode === 'change' && targetShopId) {
        payload.target_shop_id = targetShopId;
        if (changeType === 'close') {
          payload.kind = 'disable';
        }
      }

      const response = await createShopApplication(payload);

      if (response.type) {
        // Error response
        setSubmitResult('error');
        setErrorMessage(response.message || '申請の作成に失敗しました');
        setIsModalOpen(false);
      } else {
        setSubmitResult('success');
        setIsModalOpen(false);
      }
    } catch (err: any) {
      setSubmitResult('error');
      setErrorMessage(err?.message || '申請の作成に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    if (!isSubmitting) {
      setIsModalOpen(false);
      setPendingFormValues(null);
    }
  };

  const pageTitle = mode === 'create' ? '店舗追加申請' : '店舗情報変更申請';
  const crumbs = mode === 'create' ? CRUMBS_CREATE : buildChangeCrumbs(targetShopId);
  const successMessage =
    mode === 'create' ? '店舗追加申請を送信しました' : '店舗情報変更申請を送信しました';

  // This page is only for member organization users (merchants)
  if (!isMemberOrgUser) {
    return (
      <>
        <BreadCrumb crumbs={crumbs} />
        <Headings.h5>{pageTitle}</Headings.h5>
        <Wrapper>
          <Alert color="warning" isOpen>
            このページは加盟店組織ユーザー専用です。発行体ユーザーは店舗管理画面から店舗を登録してください。
          </Alert>
        </Wrapper>
      </>
    );
  }

  if (isLoadingShop) {
    return (
      <>
        <BreadCrumb crumbs={crumbs} />
        <Headings.h5>{pageTitle}</Headings.h5>
        <LoaderWrapper>
          <Loader />
        </LoaderWrapper>
      </>
    );
  }

  if (loadError) {
    return (
      <>
        <BreadCrumb crumbs={crumbs} />
        <Headings.h5>{pageTitle}</Headings.h5>
        <Wrapper>
          <Alert color="danger" isOpen>
            {loadError}
          </Alert>
        </Wrapper>
      </>
    );
  }

  return (
    <>
      <BreadCrumb crumbs={crumbs} />
      <Headings.h5>{pageTitle}</Headings.h5>
      <Wrapper>
        <form onSubmit={handleSubmit(onSubmit)}>
          <FormGrid>
            <Grid.Item size={2}>
              <SectionLabel>基本情報</SectionLabel>
            </Grid.Item>
            <Grid.Item size={8} container gridSize={2} columnGap={32} rowGap={8}>
              {/* 変更内容 - change mode only */}
              {mode === 'change' && (
                <Grid.Item flexDirection="column" nextRow size={2}>
                  <Label>変更内容</Label>
                  <RadioGroup>
                    <RadioLabel>
                      <Radio
                        name="changeType"
                        checked={changeType === 'update'}
                        setChecked={() => setChangeType('update')}
                      />
                      店舗情報変更
                    </RadioLabel>
                    <RadioLabel>
                      <Radio
                        name="changeType"
                        checked={changeType === 'close'}
                        setChecked={() => setChangeType('close')}
                      />
                      店舗廃止
                    </RadioLabel>
                  </RadioGroup>
                </Grid.Item>
              )}

              {/* 加盟するマネー */}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  加盟するマネー
                  <Required />
                </Label>
                <Controller
                  name="private_money_ids"
                  control={control}
                  rules={{
                    validate: (value) => value.length > 0 || '加盟するマネーを選択してください',
                  }}
                  render={({ field: { onChange, value } }) => (
                    <Select<PrivateMoneyOption, true, never>
                      isMulti
                      options={privateMoneyOptions}
                      value={privateMoneyOptions.filter((opt) => value.includes(opt.value.id))}
                      onChange={(opts) => onChange(opts?.map((opt) => opt.value.id) ?? [])}
                      placeholder="加盟するマネーを選択してください"
                    />
                  )}
                />
                {errors.private_money_ids && (
                  <FieldError>{errors.private_money_ids.message}</FieldError>
                )}
              </Grid.Item>

              {/* 店舗名 */}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  店舗名
                  <Required />
                </Label>
                <Controller
                  name="shop_name"
                  control={control}
                  rules={{ required: '店舗名を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth />
                  )}
                />
                {errors.shop_name && <FieldError>{errors.shop_name.message}</FieldError>}
              </Grid.Item>

              {/* 店舗名（フリガナ） */}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  店舗名（フリガナ）
                  <Required />
                </Label>
                <Controller
                  name="shop_name_furigana"
                  control={control}
                  rules={{ required: '店舗名（フリガナ）を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      syncValue={value}
                      onChange={onChange}
                      onBlur={() => onChange(toKatakana(value))}
                      fullWidth
                    />
                  )}
                />
                {errors.shop_name_furigana && (
                  <FieldError>{errors.shop_name_furigana.message}</FieldError>
                )}
              </Grid.Item>

              {/* 郵便番号 */}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  郵便番号
                  <Required />
                </Label>
                <Controller
                  name="postal_code"
                  control={control}
                  rules={{
                    required: '郵便番号を入力してください',
                    pattern: {
                      value: /^\d{7}$/,
                      message: '7桁の数字を入力してください',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={7} />
                  )}
                />
                {errors.postal_code && <FieldError>{errors.postal_code.message}</FieldError>}
              </Grid.Item>

              {/* 住所 */}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  住所
                  <Required />
                </Label>
                <Controller
                  name="address"
                  control={control}
                  rules={{ required: '住所を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth />
                  )}
                />
                {errors.address && <FieldError>{errors.address.message}</FieldError>}
              </Grid.Item>

              {/* 電話番号 */}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  電話番号
                  <Required />
                </Label>
                <Controller
                  name="tel"
                  control={control}
                  rules={{
                    required: '電話番号を入力してください',
                    pattern: {
                      value: /^\d{10,11}$/,
                      message: '10桁または11桁の数字を入力してください',
                    },
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth maxLength={11} />
                  )}
                />
                {errors.tel && <FieldError>{errors.tel.message}</FieldError>}
              </Grid.Item>

              {/* メールアドレス */}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  メールアドレス
                  <Required />
                </Label>
                <Controller
                  name="email"
                  control={control}
                  rules={{
                    required: 'メールアドレスを入力してください',
                    validate: (value) => validateEmail(value),
                  }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth />
                  )}
                />
                {errors.email && <FieldError>{errors.email.message}</FieldError>}
              </Grid.Item>

              {/* 担当者名 */}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  担当者名
                  <Required />
                </Label>
                <Controller
                  name="contact_person_name"
                  control={control}
                  rules={{ required: '担当者名を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox syncValue={value} onChange={onChange} fullWidth />
                  )}
                />
                {errors.contact_person_name && (
                  <FieldError>{errors.contact_person_name.message}</FieldError>
                )}
              </Grid.Item>

              {/* 担当者名（フリガナ） */}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  担当者名（フリガナ）
                  <Required />
                </Label>
                <Controller
                  name="contact_person_name_furigana"
                  control={control}
                  rules={{ required: '担当者名（フリガナ）を入力してください' }}
                  render={({ field: { onChange, value } }) => (
                    <TextBox
                      syncValue={value}
                      onChange={onChange}
                      onBlur={() => onChange(toKatakana(value))}
                      fullWidth
                    />
                  )}
                />
                {errors.contact_person_name_furigana && (
                  <FieldError>{errors.contact_person_name_furigana.message}</FieldError>
                )}
              </Grid.Item>

              {/* 店舗形態 */}
              <Grid.Item flexDirection="column" nextRow>
                <Label>
                  店舗形態
                  <Required />
                </Label>
                <Controller
                  name="usage_type"
                  control={control}
                  rules={{ required: '店舗形態を選択してください' }}
                  render={({ field: { onChange, value } }) => (
                    <Select<{ value: UsageType; label: string }, false, never>
                      options={USAGE_TYPE_OPTIONS}
                      value={USAGE_TYPE_OPTIONS.find((opt) => opt.value === value) ?? null}
                      onChange={(opt) => onChange(opt ? opt.value : '')}
                      placeholder="店舗形態を選択してください"
                    />
                  )}
                />
                {errors.usage_type && <FieldError>{errors.usage_type.message}</FieldError>}
              </Grid.Item>

              {/* Change mode only fields */}
              {mode === 'change' && (
                <>
                  {/* 外部ID */}
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>外部ID</Label>
                    <TextBox syncValue={externalId} disabled fullWidth />
                  </Grid.Item>

                  {/* 状態 */}
                  <Grid.Item flexDirection="column" nextRow>
                    <Label>状態</Label>
                    <Select
                      options={STATUS_OPTIONS}
                      value={STATUS_OPTIONS.find((opt) => opt.value === shopStatus) ?? null}
                      isDisabled
                    />
                  </Grid.Item>
                </>
              )}
            </Grid.Item>

            <Grid.Divider />

            <Grid.Item size={2}>
              <SectionLabel>店舗ラベル</SectionLabel>
            </Grid.Item>
            <Grid.Item size={10} flexDirection="column">
              {isMoneyEmpty ? (
                <div>先にマネーを選択してください</div>
              ) : tagGroupsLoading ? (
                <div>読み込み中...</div>
              ) : (
                <TagSelectionEditor
                  groups={availableTagGroups}
                  value={tagSelections}
                  onChange={setTagSelections}
                />
              )}
            </Grid.Item>

            <Grid.Divider />

            <Grid.Item size={12}>
              <ButtonRow>
                <Button type="submit">審査の申請</Button>
              </ButtonRow>
            </Grid.Item>
          </FormGrid>
        </form>

        <AlertWrapper>
          <Alert color="success" isOpen={submitResult === 'success'}>
            {successMessage}
          </Alert>
          <Alert color="danger" isOpen={submitResult === 'error'}>
            {errorMessage}
          </Alert>
        </AlertWrapper>
      </Wrapper>

      <ConfirmModal
        isOpen={isModalOpen}
        formValues={pendingFormValues}
        originalValues={originalValues}
        privateMoneyNames={
          pendingFormValues ? getPrivateMoneyNames(pendingFormValues.private_money_ids) : []
        }
        originalPrivateMoneyNames={
          originalValues ? getPrivateMoneyNames(originalValues.private_money_ids) : []
        }
        tagSummary={tagSummary}
        onClose={handleModalClose}
        onConfirm={handleConfirmSubmit}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default ShopApplicationForm;
